import { useState } from 'react'
import { Card, Row, Col, Badge, Button, Table, Spinner, Alert } from 'react-bootstrap'
import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { formatDateOnlyEsMX, formatDateTimeEsMX } from '../../utils/apiDateFormat'
import AccountSearchModal from '../Accounting/AccountSearchModal'
import { formatCurrency } from '../../utils/formatters'
import RevisarAsientoContableModal from './RevisarAsientoContableModal'

const getClientName = (client) => {
  if (!client) return 'N/A'
  if (client.razon_social) return client.razon_social
  if (client.business_name) return client.business_name
  const parts = []
  if (client.nombre) parts.push(client.nombre)
  if (client.apellido_paterno) parts.push(client.apellido_paterno)
  if (parts.length > 0) return ' '.join(parts)
  return client.name || 'N/A'
}

const BillDetail = ({ bill, onRefresh, onBillUpdated }) => {
  const queryClient = useQueryClient()
  const [descargando, setDescargando] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [mostrarModalCuenta, setMostrarModalCuenta] = useState(false)
  const [conceptoSeleccionadoCuenta, setConceptoSeleccionadoCuenta] = useState(null)
  const clientId = typeof bill?.client === 'object' ? bill.client?.id : (bill?.client || bill?.client_id)

  const [showAsientoModal, setShowAsientoModal] = useState(false)
  const [asientoPropuesta, setAsientoPropuesta] = useState(null)
  const [asientoPreviewError, setAsientoPreviewError] = useState(null)
  const [loadingAsientoPreview, setLoadingAsientoPreview] = useState(false)

  const procesarContableMutation = useMutation(
    async ({ lineas_contables } = {}) => {
      const response = await api.post(`/financial/bills/${bill.id}/procesar_contable/`, {
        lineas_contables: lineas_contables || [],
      })
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Factura procesada contablemente')
        queryClient.invalidateQueries('bills')
        queryClient.invalidateQueries('bills-stats-procesamiento')
        queryClient.invalidateQueries('transactions')
        setShowAsientoModal(false)
        if (onRefresh) onRefresh()
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al procesar contablemente')
      }
    }
  )

  const revertirProcesoContableMutation = useMutation(
    async () => {
      const response = await api.post(`/financial/bills/${bill.id}/revertir_proceso_contable/`)
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Proceso contable revertido correctamente')
        queryClient.invalidateQueries('bills')
        queryClient.invalidateQueries('bills-stats-procesamiento')
        queryClient.invalidateQueries('transactions')
        if (onRefresh) onRefresh()
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'No se puede revertir si la factura tiene pagos aplicados.')
      }
    }
  )

  const deleteMutation = useMutation(
    async () => {
      await api.delete(`/financial/bills/${bill.id}/`)
    },
    {
      onSuccess: () => {
        toast.success('Factura eliminada')
        queryClient.invalidateQueries('bills')
        setShowDeleteConfirm(false)
        if (onRefresh) onRefresh()
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al eliminar')
      }
    }
  )

  const actualizarCuentaConceptoMutation = useMutation(
    async ({ conceptoId, cuentaContableId }) => {
      const response = await api.post(`/financial/bills/${bill.id}/actualizar_cuenta_concepto/`, {
        concepto_id: conceptoId,
        cuenta_contable_id: cuentaContableId
      })
      return response.data
    },
    {
      onSuccess: (data) => {
        toast.success('Cuenta contable actualizada')
        queryClient.invalidateQueries('bills')
        if (onBillUpdated && data?.concepto) {
          const updatedConceptos = (bill.conceptos || []).map(c =>
            c.id === data.concepto.id ? data.concepto : c
          )
          onBillUpdated({ ...bill, conceptos: updatedConceptos })
        }
        cerrarModalCuenta()
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al actualizar cuenta')
      }
    }
  )

  const abrirModalCuenta = (concepto) => {
    setConceptoSeleccionadoCuenta(concepto)
    setMostrarModalCuenta(true)
  }

  const cerrarModalCuenta = () => {
    setMostrarModalCuenta(false)
    setConceptoSeleccionadoCuenta(null)
  }

  const seleccionarCuenta = (cuenta) => {
    if (!conceptoSeleccionadoCuenta || !cuenta?.id) {
      toast.error('Error al seleccionar cuenta')
      return
    }
    const realId = (cuenta.cuenta_base_id != null && cuenta.cuenta_base_id !== '')
      ? cuenta.cuenta_base_id
      : (typeof cuenta.id === 'number' ? cuenta.id : parseInt(cuenta.id, 10))
    actualizarCuentaConceptoMutation.mutate({
      conceptoId: conceptoSeleccionadoCuenta.id,
      cuentaContableId: realId
    })
  }

  const handleDescargarXML = async () => {
    try {
      setDescargando(true)
      const response = await api.get(`/financial/bills/${bill.id}/descargar_xml/`, {
        responseType: 'blob'
      })
      const blob = new Blob([response.data], { type: 'application/xml' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `CFDI_${bill.uuid || bill.id}.xml`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('XML descargado exitosamente')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al descargar el XML')
    } finally {
      setDescargando(false)
    }
  }

  const handleProcesarContable = async () => {
    setShowAsientoModal(true)
    setLoadingAsientoPreview(true)
    setAsientoPreviewError(null)
    setAsientoPropuesta(null)
    try {
      const res = await api.get(`/financial/bills/${bill.id}/preview-asiento-contable/`)
      setAsientoPropuesta(res.data)
    } catch (error) {
      setAsientoPreviewError(error.response?.data?.error || 'No se pudo cargar la propuesta contable')
    } finally {
      setLoadingAsientoPreview(false)
    }
  }

  const handleEliminar = () => {
    const tienePagos = bill.aplicaciones_pago?.some(a => a.estado === 'aplicado') ?? false
    if (tienePagos) {
      toast.error('No se puede eliminar: tiene pagos aplicados.')
      return
    }
    setShowDeleteConfirm(true)
  }

  const confirmEliminar = () => {
    deleteMutation.mutate()
  }

  const getStatusBadge = (status) => {
    const badges = {
      PENDIENTE: { bg: 'warning', text: 'Pendiente' },
      PARCIAL: { bg: 'info', text: 'Parcial' },
      PAGADA: { bg: 'success', text: 'Pagada' },
      CANCELADA: { bg: 'danger', text: 'Cancelada' }
    }
    const s = (status || '').toUpperCase()
    const b = badges[s] || { bg: 'secondary', text: status || 'N/A' }
    return <Badge bg={b.bg}>{b.text}</Badge>
  }

  const getFormaPagoLabel = (codigo) => {
    const map = {
      '01': '01 - Efectivo', '02': '02 - Cheque nominativo', '03': '03 - Transferencia electrónica',
      '04': '04 - Tarjeta de crédito', '05': '05 - Monedero electrónico', '06': '06 - Dinero electrónico',
      '08': '08 - Vales de despensa', '12': '12 - Dación en pago', '13': '13 - Pago por subrogación',
      '14': '14 - Pago por consignación', '15': '15 - Condonación', '17': '17 - Compensación',
      '23': '23 - Novación', '24': '24 - Confusión', '25': '25 - Remisión de deuda',
      '26': '26 - Prescripción o caducidad', '27': '27 - A satisfacción del acreedor',
      '28': '28 - Tarjeta de débito', '29': '29 - Tarjeta de servicios', '30': '30 - Aplicación de anticipos',
      '31': '31 - Intermediario pagos', '99': '99 - Por definir'
    }
    return map[codigo] || codigo || '—'
  }

  const getMetodoPagoLabel = (codigo) => {
    const map = { 'PUE': 'PUE - Pago en una sola exhibición', 'PPD': 'PPD - Pago en parcialidades o diferido' }
    return map[codigo] || codigo || '—'
  }

  if (!bill) return null

  const conceptos = bill.conceptos || []
  const aplicaciones = bill.aplicaciones_pago || []
  const xmlData = bill.xml_data || {}
  const serieFolio = bill.serie_folio || ''
  const tienePagosAplicados = aplicaciones.some(a => a.estado === 'aplicado')
  const canDelete = !tienePagosAplicados

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4>Factura Recibida <code>{bill.uuid}</code></h4>
          {serieFolio ? <div className="text-muted small">Serie-Folio: <strong>{serieFolio}</strong></div> : null}
          <div className="mt-2">
            <span className="text-muted small me-2">Estado Procesamiento:</span>
            {bill.is_processed ? <Badge bg="success">Procesado</Badge> : <Badge bg="warning">Pendiente</Badge>}
            <span className="text-muted small ms-3 me-2">Status Pago:</span>{getStatusBadge(bill.status)}
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {(bill.has_raw_xml || bill.raw_xml) && (
            <Button variant="outline-primary" size="sm" onClick={handleDescargarXML} disabled={descargando}>
              {descargando ? <><Spinner size="sm" className="me-1" /> Descargando...</> : <><i className="bi bi-file-earmark-code me-1"></i> Descargar XML</>}
            </Button>
          )}
          {!bill.is_processed && (bill.tipo_comprobante || '').toUpperCase() !== 'P' && (
            <Button
              variant="outline-info"
              size="sm"
              onClick={handleProcesarContable}
              disabled={procesarContableMutation.isLoading}
            >
              {procesarContableMutation.isLoading ? <><Spinner size="sm" className="me-1" /> Procesando...</> : <><i className="bi bi-calculator me-1"></i> Procesar contablemente</>}
            </Button>
          )}
          {(bill.tipo_comprobante || '').toUpperCase() === 'P' && (
            <Badge bg="secondary" className="align-self-center">
              Complemento de pago (REP) — el asiento se genera al aplicar el pago a la PPD
            </Badge>
          )}
          {bill.is_processed && (bill.tipo_comprobante || '').toUpperCase() !== 'P' && (
            <Button
              variant="outline-warning"
              size="sm"
              onClick={() => {
                if (window.confirm('¿Revertir proceso contable? No se puede revertir si la factura tiene pagos aplicados.')) {
                  revertirProcesoContableMutation.mutate()
                }
              }}
              disabled={revertirProcesoContableMutation.isLoading}
              title="Revertir proceso contable. No se puede si tiene pagos aplicados."
            >
              {revertirProcesoContableMutation.isLoading ? <><Spinner size="sm" className="me-1" /> Revirtiendo...</> : <><i className="bi bi-arrow-counterclockwise me-1"></i> Revertir Proceso Contable</>}
            </Button>
          )}
          {canDelete && (
            <Button variant="outline-danger" size="sm" onClick={handleEliminar} disabled={deleteMutation.isLoading}>
              {deleteMutation.isLoading ? <><Spinner size="sm" className="me-1" /> Eliminando...</> : <><i className="bi bi-trash me-1"></i> Eliminar</>}
            </Button>
          )}
        </div>
      </div>

      {/* Información general */}
      <Card className="mb-3">
        <Card.Header><h5 className="mb-0">Información general</h5></Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <p><strong>UUID:</strong> <code>{bill.uuid}</code></p>
              {serieFolio ? <p><strong>Serie-Folio:</strong> {serieFolio}</p> : null}
              <p><strong>Fecha de emisión:</strong> {formatDateOnlyEsMX(bill.emission_date, {}, 'N/A')}</p>
              <p><strong>Fecha de vencimiento:</strong> {formatDateOnlyEsMX(bill.due_date, {}, 'N/A')}</p>
              <p><strong>Origen:</strong> {bill.origen === 'manual' ? 'Manual' : 'Importado desde XML'}</p>
              {bill.condiciones_pago ? <p><strong>Condiciones de pago:</strong> {bill.condiciones_pago}</p> : null}
              {bill.moneda ? <p><strong>Moneda:</strong> {bill.moneda}</p> : null}
              {bill.exportacion ? <p><strong>Exportación (SAT):</strong> {bill.exportacion}</p> : null}
              {bill.lugar_expedicion ? <p><strong>Lugar de expedición:</strong> {bill.lugar_expedicion}</p> : null}
              {(bill.forma_pago || bill.metodo_pago) && (
                <>
                  {bill.forma_pago && <p><strong>Forma de pago:</strong> {getFormaPagoLabel(bill.forma_pago)}</p>}
                  {bill.metodo_pago && <p><strong>Método de pago:</strong> {getMetodoPagoLabel(bill.metodo_pago)}</p>}
                </>
              )}
            </Col>
            <Col md={6}>
              <p><strong>Subtotal:</strong> {formatCurrency(bill.subtotal || 0)}</p>
              <p><strong>Impuestos:</strong> {formatCurrency(bill.tax_amount || 0)}</p>
              <p><strong>Total:</strong> {formatCurrency(bill.total || 0)}</p>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Emisor (proveedor) */}
      <Card className="mb-3">
        <Card.Header><h5 className="mb-0">Emisor (Proveedor)</h5></Card.Header>
        <Card.Body>
          <p><strong>Razón Social:</strong> {bill.provider_razon_social || 'N/A'}</p>
          <p><strong>RFC:</strong> {bill.provider_rfc || 'N/A'}</p>
          {bill.provider_codigo_postal ? <p><strong>C.P.:</strong> {bill.provider_codigo_postal}</p> : null}
        </Card.Body>
      </Card>

      {/* Receptor (cliente) */}
      <Card className="mb-3">
        <Card.Header><h5 className="mb-0">Receptor (Cliente)</h5></Card.Header>
        <Card.Body>
          <p><strong>Razón Social:</strong> {bill.client_name || getClientName(bill.client) || 'N/A'}</p>
          <p><strong>RFC:</strong> {bill.client_rfc || bill.client?.rfc || 'N/A'}</p>
          {bill.receptor_direccion_display ? (
            <p><strong>Dirección:</strong> {bill.receptor_direccion_display}</p>
          ) : bill.receptor_codigo_postal ? (
            <p><strong>C.P.:</strong> {bill.receptor_codigo_postal}</p>
          ) : null}
        </Card.Body>
      </Card>

      {/* Conceptos */}
      <Card className="mb-3">
        <Card.Header><h5 className="mb-0">Conceptos</h5></Card.Header>
        <Card.Body>
          {conceptos.length > 0 ? (
            <Table responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Cantidad</th>
                  <th>Unidad</th>
                  <th>Clave unidad</th>
                  <th>No. ident.</th>
                  <th>Clave SAT</th>
                  <th>Descripción</th>
                  <th>Precio Unitario</th>
                  <th>Importe</th>
                  <th>Cuenta Contable</th>
                </tr>
              </thead>
              <tbody>
                {conceptos.map((c, idx) => (
                  <tr key={c.id || idx}>
                    <td>{idx + 1}</td>
                    <td>{parseFloat(c.cantidad || 0).toLocaleString('es-MX')}</td>
                    <td>{c.unidad_medida || '—'}</td>
                    <td>{c.clave_unidad || '—'}</td>
                    <td>{c.no_identificacion || '—'}</td>
                    <td>{c.clave_producto_servicio ? <Badge bg="info">{c.clave_producto_servicio}</Badge> : '—'}</td>
                    <td>{c.descripcion}</td>
                    <td>{formatCurrency(c.precio_unitario || 0)}</td>
                    <td>{formatCurrency(c.importe || 0)}</td>
                    <td>
                      {bill.is_processed ? (
                        c.cuenta_contable_code ? (
                          <Badge bg="success" title={c.cuenta_contable_name || ''}>{c.cuenta_contable_code}</Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )
                      ) : (
                        <Button
                          variant={c.cuenta_contable_code ? 'outline-success' : 'outline-warning'}
                          size="sm"
                          onClick={() => abrirModalCuenta(c)}
                          disabled={actualizarCuentaConceptoMutation.isLoading}
                        >
                          {c.cuenta_contable_code || 'Asignar'}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-muted">No hay conceptos registrados</p>
          )}
        </Card.Body>
      </Card>

      {/* Totales */}
      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={6}></Col>
            <Col md={6}>
              <Table>
                <tbody>
                  <tr>
                    <td><strong>Total:</strong></td>
                    <td className="text-end">
                      {formatCurrency(bill.total || 0)}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Pagado:</strong></td>
                    <td className="text-end">
                      {formatCurrency(bill.monto_pagado || 0)}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Pendiente:</strong></td>
                    <td className="text-end">
                      <strong className="text-warning">
                        {formatCurrency(bill.monto_pendiente || 0)}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Información de timbrado */}
      {bill.has_raw_xml && (
        <Card className="mb-3">
          <Card.Header><h5 className="mb-0">Información de Timbrado</h5></Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <p><strong>UUID CFDI:</strong> <Badge bg="success">{bill.uuid}</Badge></p>
                {xmlData.timbre_fecha_timbrado && (
                  <p><strong>Fecha de Timbrado:</strong> {formatDateTimeEsMX(xmlData.timbre_fecha_timbrado, undefined, '—')}</p>
                )}
                {xmlData.timbre_rfc_prov_certif && (
                  <p><strong>RFC Proveedor de Certificación:</strong> {xmlData.timbre_rfc_prov_certif}</p>
                )}
                {xmlData.lugar_expedicion && (
                  <p><strong>Lugar de Expedición:</strong> {xmlData.lugar_expedicion}</p>
                )}
              </Col>
              <Col md={6}>
                {xmlData.no_certificado ? (
                  <p><strong>Número de Certificado:</strong> {xmlData.no_certificado}</p>
                ) : (
                  <p><strong>Número de Certificado:</strong> <span className="text-muted">—</span></p>
                )}
                {xmlData.timbre_no_certificado_sat ? (
                  <p><strong>No. Certificado SAT:</strong> {xmlData.timbre_no_certificado_sat}</p>
                ) : (
                  <p><strong>No. Certificado SAT:</strong> <span className="text-muted">—</span></p>
                )}
                {xmlData.certificado ? (
                  <p><strong>Certificado:</strong> <code className="small">{xmlData.certificado}</code></p>
                ) : (
                  <p><strong>Certificado:</strong> <span className="text-muted">—</span></p>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Pagos aplicados */}
      {aplicaciones.length > 0 && (
        <Card>
          <Card.Header><h5 className="mb-0">Pagos Aplicados</h5></Card.Header>
          <Card.Body>
            <Table responsive>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Monto</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {aplicaciones.filter(a => a.estado === 'aplicado').map((a) => (
                  <tr key={a.id}>
                    <td>{formatDateTimeEsMX(a.fecha_aplicacion, undefined, '—')}</td>
                    <td>{formatCurrency(a.monto || 0)}</td>
                    <td><Badge bg="success">{a.estado}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Modal de búsqueda de cuentas contables */}
      <AccountSearchModal
        show={mostrarModalCuenta}
        onHide={cerrarModalCuenta}
        onSelect={seleccionarCuenta}
        clienteId={clientId || ''}
        initialSearch={conceptoSeleccionadoCuenta?.cuenta_contable_code || ''}
        pageSize={200}
      />

      <RevisarAsientoContableModal
        show={showAsientoModal}
        onHide={() => setShowAsientoModal(false)}
        title={`Revisar asiento — Bill ${(bill.uuid || '').slice(0, 8)}`}
        propuesta={asientoPropuesta}
        isLoading={loadingAsientoPreview}
        error={asientoPreviewError}
        clientId={clientId}
        isConfirming={procesarContableMutation.isLoading}
        confirmLabel="Aplicar asiento"
        onConfirm={({ lineas_contables }) => procesarContableMutation.mutate({ lineas_contables })}
      />

      {/* Modal confirmar eliminar */}
      {showDeleteConfirm && (
        <Alert variant="danger" className="mt-3">
          <p className="mb-2">¿Está seguro de eliminar esta factura recibida?</p>
          <div className="d-flex gap-2">
            <Button variant="danger" size="sm" onClick={confirmEliminar} disabled={deleteMutation.isLoading}>
              Confirmar eliminación
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setShowDeleteConfirm(false)}>
              Cancelar
            </Button>
          </div>
        </Alert>
      )}
    </div>
  )
}

export default BillDetail
